/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import get from 'lodash/get';
import { withNamespaces } from 'react-i18next';
import GoogleMapReact from 'google-map-react';
import supercluster from 'points-cluster';
import ReactDOM from 'react-dom';
import { compose } from 'lodash/fp';
import isEqual from 'lodash/isEqual';
import Lottie from 'react-lottie';
import 'intersection-observer'; // polyfill
import Observer from '@researchgate/react-intersection-observer';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import searchAnimation from '../../../../assets/animations/search-load-grey.json';
import { Container, Row, Column, MediaQueries } from '../../../../styles/Grid';
import { processAnalyticsResultsMapData } from '../../../../utils/analytics';
import { RICH_FILTERING as dict } from '../../../../utils/dictionarykeys';
import { hideElementCss } from '../../../../utils/responsive';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import { getDecodedQueryParams } from '../../../../utils/url';
import RichFilteringGridHero from './RichFilteringGridHero';
import { withConsumer } from '../../../../contexts';
import {
  RichFilteringConsumer,
  RICH_FILTERING_STATE,
} from '../../../../contexts/RichFilteringContext';
import { WORSKSPACE_PARAMETER } from '../../../../utils/constants';
import { Breakpoints } from '../../../../styles/Grid/GridSizes';
import createButtonCss from '../../../ui/Button/Button';
import { ZoomInUIControl, ZoomOutUIControl } from '../../../ui/Map';
import Card from '../Card';
import NoResults from '../NoResults';
import { mapStyles } from './mapStyles';
import Markers from './Markers';
import * as styles from './index.styles';
import { getCoordinates, getMapRadius } from '../../../../utils/googleMaps';

const MAP = {
  defaultZoom: 11,
  defaultCenter: { lat: 0, lng: 0 },
  options: {
    styles: mapStyles,
    zoomControl: false,
    fullscreenControl: false,
    maxZoom: 16,
    minZoom: 3,
  },
};

const animationOptions = {
  animationData: searchAnimation,
  autoplay: true,
  loop: true,
};

const locationColumns = showMap => ({
  sm: 6,
  md: 4,
  lg: [4, showMap ? 4 : 12],
  xl: [3, showMap ? 6 : 12],
});

const noLocationColumns = showMap => ({
  sm: 6,
  md: 8,
  lg: [4, showMap ? 4 : 12],
  xl: [6, showMap ? 6 : 12],
});

const SearchLottieAnimation = ({ isLoading }) => (
  <div css={styles.searchAnimationCss}>
    <Lottie
      height={40}
      isClickToPauseDisabled
      isStopped={!isLoading}
      options={animationOptions}
    />
  </div>
);

const LocationPlaceholder = ({ showMap, isSecondPlaceholder }) => (
  <Column
    css={[
      styles.cardColumn,
      isSecondPlaceholder && hideElementCss([MediaQueries.sm, MediaQueries.lg]),
    ]}
    cols={locationColumns(showMap)}
  >
    <div css={styles.locationPlaceholder}>
      <SearchLottieAnimation isLoading />
    </div>
  </Column>
);

const LocationsPlaceholder = ({ showMap }) => (
  <React.Fragment>
    <LocationPlaceholder showMap={showMap} />
    <LocationPlaceholder isSecondPlaceholder showMap={showMap} />
  </React.Fragment>
);

class RichFilteringGrid extends SafePureComponent {
  state = {
    bounds: null,
    clusters: [],
    map: null,
    mapLoad: false,
    selectedTooltip: -1,
    zoom: MAP.defaultZoom,
    zoomInControl: null,
    zoomOutControl: null,
  };

  renderLocations = () => {
    const { locations, showMap, page, viewPerPages } = get(
      this.props,
      'richFilteringContext.state',
      [],
    );
    const allowedLocations = page * viewPerPages;

    return locations.slice(0, allowedLocations).map((card, index) => (
      <Column
        css={styles.cardColumn}
        cols={locationColumns(showMap)}
        key={`result-card-${index}`}
      >
        <Card fields={card} />
      </Column>
    ));
  };

  renderNoResults = showMap => {
    const { NoResults: noResultsFields } = this.props.fields;

    return (
      <Column css={styles.cardColumn} cols={noLocationColumns(showMap)}>
        <NoResults fields={noResultsFields} rendering={this.props.rendering} />
      </Column>
    );
  };

  renderCards = () => {
    const { locations, richFilteringState, showMap } = get(
      this.props,
      'richFilteringContext.state',
      [],
    );
    const isLoading = richFilteringState === RICH_FILTERING_STATE.LOADING;

    if (isLoading) {
      return <LocationsPlaceholder showMap={showMap} />;
    }

    const renderCards =
      locations.length > 0
        ? this.renderLocations()
        : this.renderNoResults(showMap);

    return renderCards;
  };

  handleZoomOut = () => {
    const { map } = this.state;

    map && map.setZoom(map.getZoom() - 1);
  };

  handleZoomIn = () => {
    const { map } = this.state;

    map && map.setZoom(map.getZoom() + 1);
  };

  componentDidMount() {
    const { toggleMapVisibility } = get(
      this.props,
      'richFilteringContext.actions',
    );

    if (typeof document !== 'undefined') {
      const zoomInControl = document.createElement('div');
      const zoomOutControl = document.createElement('div');

      zoomInControl.className = 'zoom-in-control';
      zoomOutControl.className = 'zoom-out-control';

      this.setState({ zoomInControl, zoomOutControl });
    }

    if (window.innerWidth >= Breakpoints.lg) {
      toggleMapVisibility();
    }
  }

  // shouldRelocateMap = prevProps => {
  //   const { mapLoad } = this.state;
  //   const { params } = get(this.props, 'richFilteringContext.state', []);
  //   const { params: prevParams } = get(
  //     prevProps,
  //     'richFilteringContext.state',
  //     [],
  //   );

  //   if (
  //     (params.lat !== prevParams.lat ||
  //       params.lng !== prevParams.lng ||
  //       params.radius !== prevParams.radius ||
  //       params.ws !== prevParams.ws) &&
  //     mapLoad
  //   ) {
  //     this.setMapBounds();
  //   }
  // };

  componentDidUpdate(prevProps) {
    const { locations } = get(this.props, 'richFilteringContext.state', []);
    const { locations: prevLocations } = get(
      prevProps,
      'richFilteringContext.state',
      [],
    );
    if (!isEqual(locations, prevLocations)) {
      this.setClusters();
      this.setMapBounds();
    }

    //this.shouldRelocateMap(prevProps);
  }

  componentWillUnmount() {
    const { zoomInControl, zoomOutControl } = this.state;
    if (window.google) {
      window.google.maps.event.removeListener(
        zoomOutControl,
        'click',
        this.handleZoomOut,
      );
      window.google.maps.event.removeListener(
        zoomInControl,
        'click',
        this.handleZoomIn,
      );
    }
  }

  getDataFromApi = map => {
    const { richFilteringContext, sitecoreContext } = this.props;
    const { language } = sitecoreContext;
    const { getApiData, storeUrlParams } = richFilteringContext.actions;
    const params = getDecodedQueryParams();
    const wsValue = params[WORSKSPACE_PARAMETER];
    const id = params['id'];
    const mapBounds = map.getBounds();
    const mapCenter = map.getCenter();
    const mapCenterLat = mapCenter.lat();
    const mapCenterLng = mapCenter.lng();

    const { northeast, southwest } = getCoordinates(mapBounds);
    //getApiData({ ws: wsValue, southwest, northeast, lang: language });

    getApiData({
      ws: wsValue,
      lat: mapCenterLat || params.lat,
      lng: mapCenterLng || params.lng,
      radius: 100, //getMapRadius(mapCenterLat, mapCenterLng, mapBounds),
      lang: language,
      id: id,
    });
    storeUrlParams();
  };

  /**
   * set the  bounds where the map should be located, this bounds depends
   * on the northEast and southWest locations that comes in the url
   * @param {Object} map Map instance.
   * @param {Object} googleApi Google maps library instance.
   */
  setMapBounds = () => {
    const { map, googleApi } = this.state;
    if (!map || !googleApi) return;
    const params = getDecodedQueryParams();
    const { lat, lng } = params;
    const mapCenterLatLng = { lat: Number(lat), lng: Number(lng) };
    map.setCenter(mapCenterLatLng);
    googleApi.event.addListenerOnce(map, 'tilesloaded', () => {
      setTimeout(() => {
        const { locations } = get(this.props, 'richFilteringContext.state', []);
        let bounds = new googleApi.LatLngBounds();
        if (locations.length) {
          locations.map(loc => {
            let point = { lat: loc.lat, lng: loc.lng };
            bounds.extend(point);
          });
          map.fitBounds(bounds);
          map.panToBounds(bounds);
        }
        this.setState({ mapLoad: true });
      }, 2000);
    });
  };

  /**
   * handleMapControls: Sets map controls.
   * @param {Object} map Map instance.
   * @param {Object} googleMaps Google maps library instance.
   */
  handleMapControls(map, googleMaps) {
    const leftSideMap = map.controls[googleMaps.ControlPosition.LEFT_TOP];
    const { zoomInControl, zoomOutControl } = this.state;

    ReactDOM.render(<ZoomInUIControl />, zoomInControl);
    ReactDOM.render(<ZoomOutUIControl />, zoomOutControl);

    leftSideMap.push(zoomInControl);
    leftSideMap.push(zoomOutControl);

    googleMaps.event.addDomListener(zoomInControl, 'click', this.handleZoomIn);

    googleMaps.event.addDomListener(
      zoomOutControl,
      'click',
      this.handleZoomOut,
    );
  }

  /**
   * handleMapOnLoad: Similar to "ComponentDidMount" for google maps, it handles all related map functionality.
   * @param {Object} map Map instance.
   * @param {Object} googleApi Google maps library instance.
   */
  handleMapOnLoad(map, googleApi, locations) {
    this.getDataFromApi(map);
    this.setState({ map, googleApi });
    this.handleMapControls(map, googleApi);
    this.setMapBounds();
  }

  createClusters = () => {
    const { locations } = get(this.props, 'richFilteringContext.state', []);

    const { bounds, zoom } = this.state;
    const clusters = supercluster(locations);

    return clusters({ bounds, zoom });
  };

  iterateClusters = ({ wx, wy, numPoints, points }) => ({
    lat: wy,
    lng: wx,
    numPoints,
    points,
  });

  setClusters = () => {
    const { bounds } = this.state;
    const clusters = bounds
      ? this.createClusters().map(this.iterateClusters)
      : [];

    this.setState({ clusters });
  };

  handleMapChange = ({ zoom, bounds }) => {
    this.setState({ zoom, bounds }, () => {
      this.setClusters();
    });
  };

  onClickMarker = event => {
    const { selectedTooltip } = this.state;
    const selectedTooltipId = event.target.id;

    this.setState({
      selectedTooltip:
        selectedTooltip === selectedTooltipId ? -1 : selectedTooltipId,
    });
  };

  renderMap = () => {
    const { clusters, selectedTooltip } = this.state;
    const { locations, hoverElement } = get(
      this.props,
      'richFilteringContext.state',
      [],
    );
    const { ZoomInMap, ZoomOutMap } = this.props.fields;

    return (
      <div css={styles.mapContainer}>
        <GoogleMapReact
          yesIWantToUseGoogleMapApiInternals
          options={{
            ...MAP.options,
            // minZoom: ZoomOutMap.value,
            // maxZoom: ZoomInMap.value,
          }}
          bootstrapURLKeys={{
            language: 'en-US',
            libraries: ['places', 'geometry', 'distance matrix', 'geocoder'],
          }}
          defaultCenter={MAP.defaultCenter}
          defaultZoom={MAP.defaultZoom}
          onChange={this.handleMapChange}
          onGoogleApiLoaded={({ map, maps }) =>
            this.handleMapOnLoad(map, maps, locations)
          }
          //onTilesLoaded={this.setMapBounds}
        >
          {Markers(clusters, this.onClickMarker, selectedTooltip, hoverElement)}
        </GoogleMapReact>
      </div>
    );
  };

  onClickRefresh = () => {
    const { bounds, map } = this.state;
    const { replaceWindowUrl, getApiData } = get(
      this.props,
      'richFilteringContext.actions',
    );

    const { ws } = get(this.props, 'richFilteringContext.state.params');

    const mapCenter = map.getCenter();
    const mapBounds = map.getBounds();
    const lat = mapCenter.lat();
    const lng = mapCenter.lng();
    //console.log(mapCenter);
    const mapRadius = getMapRadius(lat, lng, mapBounds);

    const ne = mapBounds.getNorthEast();
    const sw = mapBounds.getSouthWest();

    //this.getDataFromApi(map);

    replaceWindowUrl({
      radius: mapRadius,
      search: '',
      lat: lat,
      lng: lng,
      northeast: ne,
      southwest: sw,
    });
    getApiData({
      ws: ws,
      lat: lat,
      lng: lng,
      radius: mapRadius,
      id: '',
    });

    // this.setState({ bounds: { ne: ne, sw: sw } });

    processAnalyticsResultsMapData('Map refresh button');
  };

  onHandleScroll = event => {
    const { isIntersecting } = event;

    const { page, totalPages } = get(
      this.props,
      'richFilteringContext.state',
      [],
    );

    const isPaginationCompleted = page === totalPages;

    if (isIntersecting && !isPaginationCompleted) {
      const { goNext } = get(this.props, 'richFilteringContext.actions');
      goNext();
    }
  };

  safeRender() {
    const { fields, t } = this.props;
    const { locations, showMap, richFilteringState } = get(
      this.props,
      'richFilteringContext.state',
      [],
    );

    //console.log(locations);
    const { openModal } = get(this.props, 'richFilteringContext.actions', {});
    const isLoading = richFilteringState === RICH_FILTERING_STATE.LOADING;

    return (
      <Container>
        <Row css={styles.grid}>
          <Column
            css={styles.richFilteringWrap}
            cols={{ sm: 6, md: 8, lg: 12, xl: 12 }}
          >
            <RichFilteringGridHero locations={locations} fields={fields} />
          </Column>
          <Column
            css={styles.cardsWrap(showMap)}
            cols={{
              sm: 6,
              md: 8,
              lg: showMap ? 4 : 12,
              xl: showMap ? 6 : 12,
            }}
          >
            <Row>
              {this.renderCards()}
              <Observer onChange={this.onHandleScroll} threshold={1}>
                <div css={styles.infiniteScrollReference}></div>
              </Observer>
            </Row>
          </Column>
          <Column
            css={[styles.mapWrap(showMap)]}
            cols={{ sm: 6, md: 8, lg: 8, xl: 6 }}
          >
            <button
              css={[createButtonCss('tertiary'), styles.refreshButton(showMap)]}
              onClick={this.onClickRefresh}
            >
              <span css={styles.loadingVisibility(isLoading)}>
                {t(dict.REFRESH_MAP)}
              </span>
              <SearchLottieAnimation isLoading={isLoading} />
              <i
                css={[styles.refreshIcon, styles.loadingVisibility(isLoading)]}
                className="icon-refresh"
              ></i>
            </button>
            <button
              css={[hideElementCss(MediaQueries.mdAndUp), styles.filterSmall]}
              onClick={openModal}
            >
              <span css={styles.iconFilter} className="icon-filters"></span>
            </button>
            {this.renderMap()}
          </Column>
        </Row>
      </Container>
    );
  }
}

RichFilteringGrid.displayName = 'RichFiltering/RichFilteringGrid';

export default compose(
  withNamespaces(),
  withConsumer(RichFilteringConsumer, 'richFilteringContext'),
  withSitecoreContext(),
)(RichFilteringGrid);
